import React, { useState, useEffect } from 'react';
import AmountInput from '../../../components/Utils/AmountInput';
import { apiService } from '../../../components/Config/api.service';
import { notify } from '../../../components/Utils/Notifications';
import { FormField } from '../../../components/Formulaire/FormField';
import FormSection from '../../../components/Formulaire/FormSection';

const ExternalContractForm = ({ employee = {}, currentExternal = null, onSuccess = () => {}, onCancel = () => {} }) => {
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: currentExternal?.name || '',
    type: currentExternal?.type || 'stage',
    worked_days: currentExternal?.worked_days || 30,
    date_start: currentExternal?.date_start || '',
    date_end: currentExternal?.date_end || '',
    salary: currentExternal?.salary ?? 0,
    net_salary: currentExternal?.net_salary ?? 0, 
    taxes: currentExternal?.taxes ?? 5.0,
    state: currentExternal?.state || 'active',
    employee_id: currentExternal?.employee_id || employee?.id || null,
  });

  useEffect(() => {
    // fetch current external contract details when editing
    const fetchCurrentExternal = async () => {
      if (currentExternal?.id) {
        try {
          setLoading(true);
          const res = await apiService.getElementById('/external_contract', currentExternal.id);
          const data = res.data || {};
          setForm({
            name: data.name || '',
            type: data.type || 'stage',
            worked_days: data.worked_days || 30,
            date_start: data.date_start || '',
            date_end: data.date_end || '',
            salary: data.salary ?? 0,
            net_salary: data.net_salary ?? 0, 
            taxes: data.taxes ?? 5.0,
            state: data.state || 'draft',
            employee_id: data.employee_id || employee?.id || null,
          });
          setError('');
        } catch (err) {
          console.error('Error fetching external contract:', err);
          setError('Erreur lors de la récupération du contrat externe.');
        } finally {
          setLoading(false);
        }
      } else {
        // when creating new, ensure employee id is set
        setForm(prev => ({ ...prev, employee_id: employee?.id || null }));
      }
    };
    fetchCurrentExternal();
  }, [currentExternal, employee]);

  useEffect(() => {
    if (form.type === 'stage' && form.worked_days !== 30) {
      setForm(prev => ({ ...prev, worked_days: 30 }));
    }
  }, [form.type]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleNetChange = (newValue) => {
    setForm(prev => ({ ...prev, net_salary: Number(newValue) || 0 }));
  };

  const handleTaxesChange = (e) => {
    const v = parseFloat(e.target.value);
    setForm(prev => ({ ...prev, taxes: isNaN(v) ? 0 : v }));
  };

  const computeSalary = () => {
    const net_salary= Number(form.net_salary || 0);
    const taxes = Number(form.taxes || 0);
    if (taxes >= 100) return 0;
    const salary = net_salary / (1 - taxes / 100);
    return Math.round(salary);
  };

  const computeNet = () => {
    const salary = Number(form.salary || 0);
    const taxes = Number(form.taxes || 0);
    const net_salary= salary - (salary * (taxes / 100));
    return isFinite(net_salary) ? Math.round(net_salary) : 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      setLoading(true);
      const payload = {
        name: form.name,
        type: form.type,
        worked_days: Number(form.worked_days || 0),
        date_start: form.date_start || null,
        date_end: form.date_end || null,
        net_salary: Number(form.net_salary|| 0), // <-- fournir net_salary
        taxes: Number(form.taxes || 0),
        state: form.state,
        employee: form.employee_id || employee?.id,
      };

      if (currentExternal?.id) {
        await apiService.updateElementById('/external_contract', currentExternal.id, payload);
        notify('Contrat externe mis à jour', '', 2);
        setSuccess('Contrat externe mis à jour');
      } else {
        await apiService.saveElement('/external_contract', payload);
        notify('Contrat externe créé', '', 2);
        setSuccess('Contrat externe créé');
      }

      // wait a tick to show message then callback
      setTimeout(() => onSuccess(), 300);
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <FormSection title="Informations du contrat externe" color="blue">
        <FormField label="Nom du contrat:">
          <input
            name="name"
            value={form.name || ''}
            onChange={handleChange}
            required
            className="input input-bordered w-full focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20"
          />
        </FormField>

        <FormField label="Statut:">
          <select name="state" value={form.state} onChange={handleChange} className="select select-bordered w-full">
            <option value="draft">Brouillon</option>
            <option value="active">Actif</option>
            <option value="expired">Expiré</option>
            <option value="cancel">Annulé</option>
          </select>
        </FormField>

        <FormField label="Employé:">
          <input type="text" value={employee?.name || ''} readOnly className="input input-bordered w-full" />
        </FormField>

        <FormField label="Type:">
          <select name="type" value={form.type} onChange={handleChange} className="select select-bordered w-full">
            <option value="stage">Stagiaire</option>
            <option value="prestation">Prestataire</option>
          </select>
        </FormField>
      </FormSection>

      <FormSection title="Période" color="orange">
        <FormField label="Date de début:">
          <input type="date" name="date_start" value={form.date_start || ''} onChange={handleChange} className="input input-bordered w-full" />
        </FormField>
        <FormField label="Date de fin:">
          <input type="date" name="date_end" value={form.date_end || ''} onChange={handleChange} className="input input-bordered w-full" />
        </FormField>
        <FormField label="Nombre de Jours travaillés:">
          <input
            type="number"
            name="worked_days"
            value={form.type === 'stage' ? 30 : form.worked_days}
            onChange={handleChange}
            className="input input-bordered w-full"
            readOnly={form.type === 'stage'}
          />
        </FormField>
      </FormSection>

      <FormSection title="Salaire" color="purple">
        <FormField label="Salaire net:">
          <AmountInput value={form.net_salary} onChange={handleNetChange} className="w-full input input-bordered focus:border-[--color-primary] focus:ring-2 focus:ring-[--color-primary] focus:ring-opacity-20" />
        </FormField>
        <FormField label="Taxes (%):">
          <input type="number" readOnly step="0.1" min="0" name="taxes" value={form.taxes} onChange={handleTaxesChange} className="input input-bordered w-full" />
        </FormField>
        {/* Affichage formaté */}
        <div className="w-full flex gap-4">
          <span>Salaire brut calculé:</span>
          <span className='text-[--color-primary] font-bold'>
            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(computeSalary())}
          </span>
        </div>
      </FormSection>

      <div className="flex justify-center mt-8 gap-4">
        {/* <button type="button" onClick={onCancel} disabled={loading} className="px-6 py-2 rounded bg-gray-100">Annuler</button> */}
        <button type="submit" disabled={loading} className="bg-[--color-primary] hover:bg-[--bg-hover] rounded-full text-white px-12 py-3 font-medium transition-all duration-200 shadow-sm">
          {loading ? (currentExternal ? 'Traitement...' : 'Création...') : (currentExternal ? 'Mettre à jour' : 'Créer')}
        </button>
      </div>
    </form>
  );
};

export default ExternalContractForm;